/**
 * @fileoverview Sub-category picker for a timesheet entry.
 *
 * Purpose: choose one of the accounting sub-codes defined on the entry's head
 * category, or add a code the firm uses but has not defined yet. Sub-codes are
 * optional throughout — many firms never use them, so "No sub-code" is always
 * available and is the default.
 *
 * A value already on an entry is always offered, even when it is not in the
 * category's list. Sub-categories were free text before they were structured,
 * and imports carry codes in as strings, so silently dropping an unrecognised
 * value would rewrite history the moment someone opened an old entry.
 *
 * @component
 * @example
 * <SubCategoryPicker
 *   categoryId={form.categoryId}
 *   categories={context.categories}
 *   value={form.subCategory}
 *   onChange={(next) => setForm((p) => ({ ...p, subCategory: next }))}
 * />
 */

import { useMutation } from "convex/react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { SearchableSelect } from "@/ui/composed/inputs/searchable-select";
import { extractErrorMessage } from "@/utils/errors";

/** Sentinel for "no sub-code", which is a valid and common answer. */
const NO_SUB_CODE = "";

export type SubCategoryCategory = {
  _id: Id<"timeCategories">;
  name: string;
  subCodes: Array<{ code: string; name?: string }>;
};

export function SubCategoryPicker({
  categoryId,
  categories,
  value,
  onChange,
  disabled,
  className,
}: {
  /** The entry's head category. Empty while the user has not picked one. */
  categoryId: Id<"timeCategories"> | "";
  categories: SubCategoryCategory[];
  /** Current sub-code, or "" for none. */
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  className?: string;
}) {
  const addSubCode = useMutation(api.timesheets.categories.addSubCode);

  const category = categories.find((c) => c._id === categoryId);
  const defined = category?.subCodes ?? [];

  const options = defined.map((s) => ({
    value: s.code,
    label: s.name ? `${s.code} — ${s.name}` : s.code,
    title: s.name,
  }));
  if (value && !defined.some((s) => s.code === value)) {
    options.push({
      value,
      label: value,
      title: `Not in ${category?.name ?? "this category"}'s sub-codes`,
    });
  }

  const handleCreate = async (raw: string) => {
    if (!categoryId) return;
    const code = raw.trim().toUpperCase();
    try {
      await addSubCode({ categoryId, code });
      onChange(code);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not add that sub-code."));
    }
  };

  return (
    <SearchableSelect
      value={value}
      onValueChange={(next) => onChange(next === NO_SUB_CODE ? "" : next)}
      options={options}
      // Doubles as the reason the control is inert: a disabled trigger
      // showing "No sub-code" would look like a bug rather than a prompt.
      allOptionLabel={categoryId ? "No sub-code" : "Pick a category first"}
      allOptionValue={NO_SUB_CODE}
      searchPlaceholder="Search or add a sub-code…"
      emptyMessage="No sub-codes yet."
      disabled={disabled || !categoryId}
      onCreateOption={categoryId ? (raw) => void handleCreate(raw) : undefined}
      createOptionLabel={(raw) =>
        `Add "${raw.trim().toUpperCase()}" to ${category?.name ?? "this category"}`
      }
      className={className}
    />
  );
}
